/**
 * Monthly Earnings Page
 * Refactored main component - uses sub-components from ./monthly-earnings/
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useTaxYear } from '../context/TaxYearContext';
import { Upload, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

// Import refactored sub-components
import {
  SummaryCards,
  EarningsChart,
  MonthlyBreakdownRow,
  AnnualBreakdownRow,
  PreviewDialog,
  EditDialog,
  DeleteDialog,
  PasswordPromptDialog,
  calculateMonthlyData,
  calculateAnnualData,
  getTripsForMonth
} from './monthly-earnings';
import UploadDialog from '../components/monthly-earnings/UploadDialog';

// Import TripDialog for editing trips
import TripDialog from '../components/trips/TripDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MonthlyEarnings = () => {
  const navigate = useNavigate();
  
  // Data state
  const [trips, setTrips] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gbpReceivedData, setGbpReceivedData] = useState({});
  
  // UI state
  const { selectedYear, setSelectedYear } = useTaxYear();
  const [taxYears, setTaxYears] = useState([]);
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [comparisonData, setComparisonData] = useState({});
  
  // Dialog state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadMonth, setUploadMonth] = useState(null);
  const [uploadDocType, setUploadDocType] = useState('wage_slip');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [pdfPassword, setPdfPassword] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [deleteConfirmDoc, setDeleteConfirmDoc] = useState(null);
  const [extractingDoc, setExtractingDoc] = useState(null);
  const [passwordPrompt, setPasswordPrompt] = useState(null);
  const [savePasswordDoc, setSavePasswordDoc] = useState(null);
  
  // Trip editing state
  const [editingTrip, setEditingTrip] = useState(null);
  const [tripDialogOpen, setTripDialogOpen] = useState(false);
  
  const fileInputRef = useRef(null);

  // Fetch tax years on mount
  const fetchTaxYears = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/tax-years`);
      setTaxYears(response.data.tax_years);
      if (response.data.tax_years.length > 0 && (!selectedYear || selectedYear === 'all')) {
        setSelectedYear(response.data.tax_years[0]);
      }
    } catch (error) {
    }
  }, [selectedYear, setSelectedYear]);

  const fetchData = useCallback(async () => {
    try {
      const [tripsRes, docsRes, gbpRes, contractsRes] = await Promise.all([
        axios.get(`${API_URL}/api/trips`),
        axios.get(`${API_URL}/api/documents`),
        axios.get(`${API_URL}/api/monthly-gbp-received`),
        axios.get(`${API_URL}/api/contracts`)
      ]);
      setTrips(tripsRes.data.trips || []);
      setDocuments(docsRes.data.documents || []);
      setGbpReceivedData(gbpRes.data || {});
      setContracts(contractsRes.data.contracts || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchComparisonData = useCallback(async (monthKey) => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/comparison/${monthKey}`);
      setComparisonData(prev => ({
        ...prev,
        [monthKey]: response.data
      }));
    } catch (error) {
    }
  }, []);

  const fetchGbpReceivedData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/monthly-gbp-received`);
      setGbpReceivedData(response.data || {});
    } catch (error) {
    }
  };

  useEffect(() => {
    fetchTaxYears();
  }, [fetchTaxYears]);

  useEffect(() => {
    fetchData();
  }, [selectedYear, fetchData]);

  // Fetch comparison data when month is expanded
  useEffect(() => {
    if (expandedMonth && !comparisonData[expandedMonth]) {
      fetchComparisonData(expandedMonth);
    }
  }, [expandedMonth, comparisonData, fetchComparisonData]);

  // Generate month options for upload dialog
  const monthOptions = React.useMemo(() => {
    if (!selectedYear) return [];
    const startYear = parseInt(selectedYear.split('/')[0]);
    const months = [];
    for (let m = 4; m <= 12; m++) {
      const key = `${startYear}-${String(m).padStart(2, '0')}`;
      months.push({ value: key, label: new Date(startYear, m - 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' }) });
    }
    for (let m = 1; m <= 3; m++) {
      const key = `${startYear + 1}-${String(m).padStart(2, '0')}`;
      months.push({ value: key, label: new Date(startYear + 1, m - 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' }) });
    }
    return months;
  }, [selectedYear]);

  // Document handlers
  const handleExtractData = async (docId, password = null) => {
    setExtractingDoc(docId);
    try {
      await axios.post(`${API_URL}/api/documents/${docId}/extract`, { password });
      toast.success('Data extracted successfully');
      setPasswordPrompt(null);
      await fetchData();
      if (expandedMonth) {
        await fetchComparisonData(expandedMonth);
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || '';
      if (errorDetail.includes('password-protected') || errorDetail.includes('password')) {
        // Show password prompt
        const doc = documents.find(d => d.id === docId);
        setPasswordPrompt({ docId, docName: doc?.file_name || 'document' });
      } else {
        toast.error(errorDetail || 'Failed to extract data');
      }
    } finally {
      setExtractingDoc(null);
    }
  };

  // Handler for extract with password prompt - directly shows password dialog
  const handleExtractWithPassword = (doc) => {
    setPasswordPrompt({ docId: doc.id, docName: doc.file_name });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Content = e.target.result.split(',')[1];
        
        // Determine tax year and month
        let taxYear = selectedYear;
        let monthValue = null;
        const isAnnual = uploadMonth === 'annual' || !uploadMonth;

        if (!isAnnual && uploadMonth) {
          const [year, month] = uploadMonth.split('-');
          const monthNum = parseInt(month);
          taxYear = monthNum >= 4 
            ? `${year}/${parseInt(year) + 1}` 
            : `${parseInt(year) - 1}/${year}`;
          monthValue = uploadMonth;
        }

        const response = await axios.post(`${API_URL}/api/documents`, {
          file_name: file.name,
          file_content: base64Content,
          tax_year: taxYear,
          document_type: uploadDocType,
          month: monthValue,
          month_is_work_month: !isAnnual,
          password: pdfPassword || null
        });

        if (response.data.extraction?.extracted_data) {
          toast.success('Document uploaded and data extracted!');
        } else if (response.data.extraction?.status === 'processing') {
          toast.success('Document uploaded! Data extraction is running in the background.');
        } else if (response.data.extraction?.error) {
          if (response.data.extraction.error.includes('password')) {
            toast.warning('Uploaded, but PDF is password-protected. Use Extract to retry with password.');
          } else {
            toast.warning('Uploaded, but extraction failed. You can retry later.');
          }
        } else {
          toast.success('Document uploaded successfully');
        }

        setUploadDialogOpen(false);
        setUploadMonth(null);
        setUploadDocType('wage_slip');
        setPdfPassword('');
        fetchData();
        if (expandedMonth) {
          setTimeout(() => fetchComparisonData(expandedMonth), 500);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDownload = async (docId, fileName) => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/${docId}`);
      const doc = response.data;
      
      const link = document.createElement('a');
      link.href = `data:application/octet-stream;base64,${doc.file_content}`;
      link.download = fileName;
      link.click();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handlePreview = async (docId) => {
    try {
      const response = await axios.get(`${API_URL}/api/documents/${docId}`);
      setPreviewDoc(response.data);
    } catch (error) {
      toast.error('Failed to load document');
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      await axios.delete(`${API_URL}/api/documents/${docId}`);
      toast.success('Document deleted successfully');
      setDeleteConfirmDoc(null);
      await fetchData();
      if (expandedMonth) {
        setComparisonData(prev => {
          const updated = { ...prev };
          delete updated[expandedMonth];
          return updated;
        });
        await fetchComparisonData(expandedMonth);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleEditDocument = async () => {
    if (!editingDoc) return;
    
    try {
      await axios.put(`${API_URL}/api/documents/${editingDoc.id}`, {
        file_name: editingDoc.file_name,
        tax_year: editingDoc.tax_year,
        document_type: editingDoc.document_type,
        month: editingDoc.month
      });
      toast.success('Document updated successfully');
      setEditingDoc(null);
      await fetchData();
      setComparisonData({});
      if (expandedMonth) {
        await fetchComparisonData(expandedMonth);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update document');
    }
  };

  // Helper functions
  const getDocumentsForMonth = (monthKey) => {
    return documents.filter(doc => doc.month === monthKey);
  };

  // Trip handlers
  const handleEditTrip = (trip) => {
    // Find the full trip data from the trips array
    const fullTrip = trips.find(t => t.id === trip.id);
    if (fullTrip) {
      setEditingTrip(fullTrip);
      setTripDialogOpen(true);
    }
  };

  const handleViewTrip = (trip) => {
    // Navigate to Trips page with the trip highlighted
    navigate(`/trips?highlight=${trip.id}`);
  };

  const handleTripEditSuccess = async () => {
    setTripDialogOpen(false);
    setEditingTrip(null);
    toast.success('Trip updated successfully');
    await fetchData();
    // Clear comparison data so it refreshes
    setComparisonData({});
    if (expandedMonth) {
      await fetchComparisonData(expandedMonth);
    }
  };

  const handleTripDialogClose = () => {
    setTripDialogOpen(false);
    setEditingTrip(null);
  };

  // Calculate monthly data
  const isAllYears = !selectedYear || selectedYear === 'all';
  const monthlyData = isAllYears ? [] : calculateMonthlyData(trips, selectedYear, contracts);
  const annualData = isAllYears ? calculateAnnualData(trips, taxYears, contracts) : [];
  
  const totalYearEarnings = isAllYears
    ? annualData.reduce((sum, y) => sum + y.totalEarnings, 0)
    : monthlyData.reduce((sum, m) => sum + m.earnings, 0);

  // Aggregate tax/NI/pension from wage slips
  const taxTotals = React.useMemo(() => {
    const wageSlips = documents.filter(d => d.document_type === 'wage_slip' && d.extracted_data);
    
    // Filter by selected tax year if not 'all'
    const filtered = isAllYears ? wageSlips : wageSlips.filter(d => {
      if (d.tax_year === selectedYear) return true;
      if (d.month) {
        const [y, m] = d.month.split('-').map(Number);
        const startYear = parseInt(selectedYear?.split('/')[0] || '0');
        if (m >= 4) return y === startYear;
        return y === startYear + 1;
      }
      return false;
    });
    
    let tax = 0, ni = 0, pension = 0;
    filtered.forEach(d => {
      tax += d.extracted_data.income_tax || 0;
      ni += d.extracted_data.national_insurance || 0;
      pension += d.extracted_data.pension_contribution || 0;
    });
    
    return { tax, ni, pension };
  }, [documents, selectedYear, isAllYears]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="monthly-earnings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Pay & Contracts</h1>
          <p className="text-slate-400">View your earnings breakdown by tax year</p>
        </div>
      </div>

      {/* Summary Cards */}
      <SummaryCards 
        totalYearEarnings={totalYearEarnings}
        taxTotals={taxTotals}
        isAllYears={isAllYears}
      />

      {/* Chart - show annual chart for all years, monthly for specific year */}
      {isAllYears ? (
        annualData.length > 0 && (
          <EarningsChart 
            monthlyData={annualData.map(y => ({ month: y.taxYear, earnings: y.totalEarnings }))} 
            title="Earnings by Tax Year"
          />
        )
      ) : (
        <EarningsChart monthlyData={monthlyData} title="Earnings by Month" />
      )}

      {/* Annual Breakdown - shown when All Years selected */}
      {isAllYears && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Annual Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {annualData.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {annualData.map((yearData) => (
                  <AnnualBreakdownRow
                    key={yearData.taxYear}
                    yearData={yearData}
                    onSelect={(year) => setSelectedYear(year)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-slate-400 text-center py-8">No tax year data available. Add trips to see your annual breakdown.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Monthly Breakdown - 4x3 Grid (hidden when All Years selected) */}
      {!isAllYears && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Monthly Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {monthlyData.map((monthData) => {
                const tripsForMonth = getTripsForMonth(trips, monthData.monthStart, monthData.monthEnd, contracts);
                const docsForMonth = getDocumentsForMonth(monthData.monthKey);
                const isExpanded = expandedMonth === monthData.monthKey;
                const comparison = comparisonData[monthData.monthKey];
                
                return (
                  <MonthlyBreakdownRow
                    key={monthData.monthKey}
                    monthData={monthData}
                    selectedYear={selectedYear}
                    tripsForMonth={tripsForMonth}
                    docsForMonth={docsForMonth}
                    isExpanded={isExpanded}
                    comparison={comparison}
                    extractingDoc={extractingDoc}
                    gbpReceivedData={gbpReceivedData}
                    onGbpReceivedUpdate={fetchGbpReceivedData}
                    onToggleExpand={() => setExpandedMonth(isExpanded ? null : monthData.monthKey)}
                    onUploadClick={(e) => {
                      e.stopPropagation();
                      setUploadMonth(monthData.monthKey);
                      setUploadDialogOpen(true);
                    }}
                    onPreview={handlePreview}
                    onDownload={handleDownload}
                    onExtract={handleExtractData}
                    onEdit={(doc) => setEditingDoc({
                      id: doc.id,
                      file_name: doc.file_name,
                      tax_year: doc.tax_year,
                      document_type: doc.document_type,
                      month: doc.month
                    })}
                    onDelete={setDeleteConfirmDoc}
                    onEditTrip={handleEditTrip}
                    onViewTrip={handleViewTrip}
                    onExtractWithPassword={handleExtractWithPassword}
                    onSavePassword={(doc) => setSavePasswordDoc(doc)}
                  />
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      <UploadDialog
        open={uploadDialogOpen}
        onClose={() => { setUploadDialogOpen(false); setUploadMonth(null); setUploadDocType('wage_slip'); setPdfPassword(''); }}
        selectedMonth={uploadMonth}
        onMonthChange={setUploadMonth}
        monthOptions={monthOptions}
        onFileSelect={handleFileUpload}
        uploading={uploadLoading}
        fileInputRef={fileInputRef}
        pdfPassword={pdfPassword}
        onPasswordChange={setPdfPassword}
        documentType={uploadDocType}
        onDocumentTypeChange={setUploadDocType}
      />

      <PreviewDialog
        previewDoc={previewDoc}
        onClose={() => setPreviewDoc(null)}
        onDownload={handleDownload}
        onPasswordSaved={fetchData}
      />

      <EditDialog
        editingDoc={editingDoc}
        setEditingDoc={setEditingDoc}
        onSave={handleEditDocument}
      />

      <DeleteDialog
        deleteConfirmDoc={deleteConfirmDoc}
        setDeleteConfirmDoc={setDeleteConfirmDoc}
        onDelete={handleDeleteDocument}
      />

      {/* Password Prompt Dialog for protected PDFs */}
      <PasswordPromptDialog
        open={!!passwordPrompt}
        onClose={() => setPasswordPrompt(null)}
        documentName={passwordPrompt?.docName || ''}
        documentId={passwordPrompt?.docId || ''}
        loading={!!extractingDoc}
        onSubmit={async (password, shouldSave) => {
          if (passwordPrompt?.docId) {
            // Save password to document if checkbox is checked
            if (shouldSave) {
              try {
                await axios.put(`${API_URL}/api/documents/${passwordPrompt.docId}/password`, { password });
              } catch {
                // Non-critical — extraction still proceeds
              }
            }
            handleExtractData(passwordPrompt.docId, password);
          }
        }}
      />

      {/* Trip Edit Dialog */}
      <Dialog open={tripDialogOpen} onOpenChange={setTripDialogOpen}>
        <TripDialog
          trip={editingTrip}
          existingTrips={trips}
          onSuccess={handleTripEditSuccess}
          onClose={handleTripDialogClose}
        />
      </Dialog>

      {/* Save Password Dialog for existing documents */}
      <PasswordPromptDialog
        open={!!savePasswordDoc}
        onClose={() => setSavePasswordDoc(null)}
        documentName={savePasswordDoc?.file_name || ''}
        documentId={savePasswordDoc?.id || ''}
        loading={false}
        mode="save"
        onSubmit={async (password, shouldSave) => {
          if (savePasswordDoc?.id && shouldSave) {
            try {
              await axios.put(`${API_URL}/api/documents/${savePasswordDoc.id}/password`, { password });
              toast.success('Password saved for this document');
              fetchData();
            } catch {
              toast.error('Failed to save password');
            }
          }
          setSavePasswordDoc(null);
        }}
      />
    </div>
  );
};

export default MonthlyEarnings;
